import { useRef, useState } from "react";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import Reveal from "../components/ui/Reveal";
import {
  HiOutlineMail,
  HiOutlinePhone,
  HiOutlineLocationMarker
} from "react-icons/hi";
import {
  FaInstagram,
  FaTiktok,
  FaFacebookF,
  FaWhatsapp
} from "react-icons/fa";
import heroimg from "../assets/a17.png";
import ScrollToTop from "../components/ui/ScrollToTop";
import SEO from "../components/SEO";

const Contact = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  });

  // Estado para el formulario
  const [formData, setFormData] = useState({
    nombre: "",
    telefono: "",
    servicio: "Saneamiento Físico Legal",
    mensaje: ""
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleWhatsAppSubmit = (e) => {
    e.preventDefault();

    // Tu número de WhatsApp (formato internacional sin el +)
    const phoneNumber = "51983974833";

    // Construimos el mensaje con formato profesional
    const message = `*NUEVA SOLICITUD - PLANIX*%0A%0A` +
      `*Nombre:* ${formData.nombre}%0A` +
      `*WhatsApp:* ${formData.telefono}%0A` +
      `*Servicio:* ${formData.servicio}%0A` +
      `*Mensaje:* ${formData.mensaje}`;

    // Abrimos WhatsApp en una nueva pestaña
    window.open(`https://wa.me/${phoneNumber}?text=${message}`, "_blank");
  };


  const yRaw = useTransform(scrollYProgress, [0, 1], [-100, 100]);
  const y = useSpring(yRaw, { stiffness: 100, damping: 30 });

  /*
  const socialMedia = [
    { name: "WhatsApp", icon: <FaWhatsapp />, link: "https://wa.me/51920238477", color: "hover:text-[#25D366]" },
    { name: "Instagram", icon: <FaInstagram />, link: "#", color: "hover:text-[#E1306C]" },
    { name: "TikTok", icon: <FaTiktok />, link: "#", color: "hover:text-black" },
    { name: "Facebook", icon: <FaFacebookF />, link: "#", color: "hover:text-[#1877F2]" },
  ];*/

  return (
    <div className="pt-32 bg-white">
      <SEO
        title="Contacto | Saneamiento Físico Legal - Planix"
        description="Contacta a Planix para servicios de saneamiento físico legal, independización, topografía y planos técnicos en Lima y San Juan de Lurigancho."
        type="website"
      />

      <ScrollToTop />

      {/* HERO PERSONALIZADO */}
      <section ref={ref} className="relative h-[60vh] flex items-center justify-center overflow-hidden bg-[#1C1C1C]">
        <motion.div style={{ y }} className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-white z-10" />
          <img src={heroimg} alt="Ingeniería Planix" className="w-full h-[120%] object-cover opacity-70" />
        </motion.div>

        <div className="relative z-20 text-center px-6">
          <Reveal>
            <span className="text-[#F26B1D] font-bold tracking-[0.3em] uppercase text-xs mb-4 block">
              Contacto Directo
            </span>
            <h1 className="text-5xl md:text-7xl font-black text-white leading-tight">
              Construyamos <br /> <span className="text-[#F26B1D]">Tu Futuro</span>
            </h1>
          </Reveal>
        </div>
      </section>

      {/* CUERPO DE CONTACTO */}
      <section className="py-24 max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-12 gap-16">

          {/* COLUMNA IZQUIERDA: INFO & REDES */}
          <div className="lg:col-span-5 space-y-12">
            <Reveal>
              <h2 className="text-4xl font-bold text-[#1C1C1C] leading-tight">
                ¿Tienes un proyecto en mente? <br />
                <span className="text-gray-400 text-2xl font-medium">Hablemos de ingeniería y soluciones legales.</span>
              </h2>
            </Reveal>

            <div className="space-y-8">
              {[
                { icon: <HiOutlinePhone />, title: "Llámanos", info: "+51 983 974 833" },
                { icon: <HiOutlineMail />, title: "Escríbenos", info: "contacto@planix.pe" },
                { icon: <HiOutlineLocationMarker />, title: "Visítanos", info: "San Juan de Lurigancho, Lima, Perú" },
              ].map((item, idx) => (
                <Reveal key={idx}>
                  <div className="flex items-center gap-5 group">
                    <div className="w-12 h-12 rounded-full bg-orange-50 flex items-center justify-center text-[#F26B1D] text-2xl group-hover:bg-[#F26B1D] group-hover:text-white transition-colors duration-300">
                      {item.icon}
                    </div>
                    <div>
                      <h4 className="text-sm font-bold uppercase tracking-wider text-gray-400">{item.title}</h4>
                      <p className="text-lg font-semibold text-[#1C1C1C]">{item.info}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>

            {/* SECCIÓN REDES SOCIALES - DISEÑO IMPACTANTE */}
            <div className="pt-12 border-t border-gray-100">
              <Reveal>
                <h4 className="text-sm font-black uppercase tracking-[0.2em] text-[#1C1C1C] mb-8 flex items-center gap-2">
                  <span className="w-8 h-[2px] bg-[#F26B1D]"></span>
                  Nuestras Redes Oficiales
                </h4>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  {[
                    {
                      name: "WhatsApp",
                      icon: <FaWhatsapp />,
                      link: "https://wa.me/51983974833",
                      bg: "bg-[#25D366]/10",
                      text: "text-[#25D366]",
                      hover: "hover:bg-[#25D366]"
                    },
                    {
                      name: "Instagram",
                      icon: <FaInstagram />,
                      link: "#",
                      bg: "bg-[#E1306C]/10",
                      text: "text-[#E1306C]",
                      hover: "hover:bg-gradient-to-tr from-[#f9ce34] via-[#ee2a7b] to-[#6228d7]"
                    },
                    {
                      name: "TikTok",
                      icon: <FaTiktok />,
                      link: "#",
                      bg: "bg-black/5",
                      text: "text-black",
                      hover: "hover:bg-black"
                    },
                    {
                      name: "Facebook",
                      icon: <FaFacebookF />,
                      link: "#",
                      bg: "bg-[#1877F2]/10",
                      text: "text-[#1877F2]",
                      hover: "hover:bg-[#1877F2]"
                    },
                  ].map((social, i) => (
                    <motion.a
                      key={i}
                      href={social.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      whileHover={{ y: -8, scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className={`flex flex-col items-center justify-center p-6 rounded-2xl transition-all duration-300 group ${social.bg}`}
                    >
                      <div className={`text-4xl mb-2 transition-colors duration-300 ${social.text} group-hover:text-white`}>
                        {social.icon}
                      </div>
                      <span className={`text-[10px] font-black uppercase tracking-widest transition-colors duration-300 ${social.text} group-hover:text-white`}>
                        {social.name}
                      </span>

                      {/* Efecto de fondo al hacer hover */}
                      <div className={`absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10 ${social.hover}`}></div>
                    </motion.a>
                  ))}
                </div>

                <p className="mt-8 text-sm text-gray-400 italic">
                  Síguenos para ver nuestros últimos proyectos de <strong>independización y planos</strong> en tiempo real.
                </p>
              </Reveal>
            </div>
          </div>

          {/* COLUMNA DERECHA: FORMULARIO GLASSMORPHISM */}
          <div className="lg:col-span-7">
            <Reveal>
              <div className="relative p-1 bg-gradient-to-br from-orange-400 to-orange-600 rounded-3xl shadow-2xl">
                <div className="bg-white p-8 md:p-12 rounded-[1.4rem]">
                  <form onSubmit={handleWhatsAppSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label className="text-xs font-bold uppercase text-gray-500 ml-1">Nombre Completo</label>
                        <input
                          type="text"
                          name="nombre"
                          required
                          value={formData.nombre}
                          onChange={handleChange}
                          className="w-full bg-gray-50 border-none rounded-xl px-5 py-4 focus:ring-2 focus:ring-[#F26B1D] outline-none transition-all"
                          placeholder="Ej. Juan Pérez"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-xs font-bold uppercase text-gray-500 ml-1">WhatsApp / Teléfono</label>
                        <input
                          type="tel"
                          name="telefono"
                          required
                          value={formData.telefono}
                          onChange={handleChange}
                          className="w-full bg-gray-50 border-none rounded-xl px-5 py-4 focus:ring-2 focus:ring-[#F26B1D] outline-none transition-all"
                          placeholder="+51 000 000 000"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="text-xs font-bold uppercase text-gray-500 ml-1">Tipo de Servicio</label>
                      <select
                        name="servicio"
                        value={formData.servicio}
                        onChange={handleChange}
                        className="w-full bg-gray-50 border-none rounded-xl px-5 py-4 focus:ring-2 focus:ring-[#F26B1D] outline-none transition-all appearance-none text-gray-600"
                      >
                        <option>Saneamiento Físico Legal</option>
                        <option>Independización y Subdivisión</option>
                        <option>Declaratoria de Fábrica</option>
                        <option>Elaboración de Planos Técnicos</option>
                      </select>
                    </div>

                    <div className="space-y-2">
                      <label className="text-xs font-bold uppercase text-gray-500 ml-1">Detalles del Proyecto</label>
                      <textarea
                        name="mensaje"
                        required
                        value={formData.mensaje}
                        onChange={handleChange}
                        rows="4"
                        className="w-full bg-gray-50 border-none rounded-xl px-5 py-4 focus:ring-2 focus:ring-[#F26B1D] outline-none transition-all"
                        placeholder="Cuéntanos la ubicación y estado actual de tu predio..."
                      />
                    </div>

                    <motion.button
                      whileTap={{ scale: 0.98 }}
                      type="submit"
                      className="w-full bg-[#F26B1D] text-white py-4 rounded-xl font-bold text-lg shadow-lg shadow-orange-200 hover:bg-[#d95a16] transition-all flex items-center justify-center gap-2"
                    >
                      <FaWhatsapp className="text-2xl" />
                      Enviar a WhatsApp
                    </motion.button>
                  </form>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* MAPA ESTILIZADO */}
      <section className="pb-24 px-6">
        <div className="max-w-7xl mx-auto h-[400px] rounded-3xl overflow-hidden shadow-inner bg-gray-100 border-4 border-white">
          <iframe
            title="Ubicación de Planix en Google Maps"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d124864.91266014293!2d-77.06730105!3d-11.9723795!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x9105c5f0134446e3%3A0x6d1159938666352!2sSan%20Juan%20de%20Lurigancho!5e0!3m2!1ses-419!2spe!4v1710000000000"
            width="100%"
            height="100%"
            style={{ border: 0, filter: "grayscale(1) contrast(1.2) invert(0.05)" }}
            allowFullScreen=""
            loading="lazy"
          ></iframe>
        </div>
      </section>
    </div>
  );
};

export default Contact;